import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  CreditCard,
  Bell,
  BookOpen,
  TrendingUp,
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Send,
  Shield,
  Home,
  LogOut,
  Dumbbell,
  UserCheck,
  AlertCircle,
  Search,
  Filter,
  Download,
  Eye,
  X,
  Check,
  Calendar,
  Mail,
  Phone,
  Clock,
  Settings,
  Package,
  FileText,
  MessageSquare
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminRoute from '../components/AdminRoute';
import { useAdminStatus } from '../hooks/useAdminStatus';
import * as adminService from '../services/adminService';
import { getRateLimitStatus, shouldAllowOperation, recordOperation } from '../utils/rateLimiter';
import ClientManagementModal from '../components/ClientManagementModal';
import BlogEditor from '../components/admin/BlogEditor';
import { isStripeConfigured } from '../lib/stripe';
import { calculateRenewalDate, formatRenewalDate, formatRenewalPeriod, formatServiceTerm, getServicePriceSuffix } from '../utils/paymentUtils';

/**
 * Admin Dashboard Page
 * Secure administrative dashboard for managing clients, payments, notices, and programs
 * Fully restricted to admin role users only
 */
export default function Admin() {
  return (
    <AdminRoute>
      <AdminContent />
    </AdminRoute>
  );
}

function AdminContent() {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const { refresh: refreshAdminStatus } = useAdminStatus();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [paymentFilters, setPaymentFilters] = useState({});
  const [notices, setNotices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [services, setServices] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState('');
  const [rateLimitWarning, setRateLimitWarning] = useState('');
  const [rateLimitStatus, setRateLimitStatus] = useState({});

  useEffect(() => {
    // Refresh profile and admin status when accessing admin page
    if (user) {
      refreshProfile();
      refreshAdminStatus();
    }
    loadAdminData();
    updateRateLimitStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user, not the refresh functions

  // Update rate limit status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      updateRateLimitStatus();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const updateRateLimitStatus = () => {
    setRateLimitStatus({
      emailLookup: getRateLimitStatus('email_lookup'),
      adminOperation: getRateLimitStatus('admin_operation'),
      roleChange: getRateLimitStatus('role_change'),
    });
  };

  const loadAdminData = async () => {
    if (!user) return;
    setIsLoading(true);
    setError('');

    try {
      const [summaryResult, clientsResult, paymentsResult, noticesResult, programsResult, servicesResult, blogsResult, contactsResult] = await Promise.all([
        adminService.getAdminSummary(user.id),
        adminService.getAllClients(user.id),
        adminService.getAllPayments(user.id, {
          page: paymentsPagination.page,
          pageSize: paymentsPagination.pageSize,
          ...paymentFilters,
        }),
        adminService.getAllNotices(user.id),
        adminService.getAllPrograms(user.id),
        adminService.getAllServices(user.id),
        adminService.getAllBlogs(user.id),
        adminService.getAllContacts(user.id),
      ]);

      if (summaryResult.success) setSummary(summaryResult.data);
      if (clientsResult.success) setClients(clientsResult.data);
      if (paymentsResult.success) {
        setPayments(paymentsResult.data);
        if (paymentsResult.pagination) {
          setPaymentsPagination(paymentsResult.pagination);
        }
      }
      if (noticesResult.success) setNotices(noticesResult.data);
      if (programsResult.success) setPrograms(programsResult.data);
      if (servicesResult.success) setServices(servicesResult.data);
      if (blogsResult.success) setBlogs(blogsResult.data);
      if (contactsResult.success) setContacts(contactsResult.data);

      // Check for rate limit errors
      if (clientsResult.error && clientsResult.error.includes('Rate limit')) {
        setRateLimitWarning(clientsResult.error);
      } else {
        setRateLimitWarning('');
      }
    } catch (err) {
      setError('Failed to load admin data. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
      updateRateLimitStatus();
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'services', label: 'Services', icon: Package },
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'notices', label: 'Notices', icon: Bell },
    { id: 'programs', label: 'Programs', icon: BookOpen },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-red-900/95 backdrop-blur-md shadow-lg border-b-2 border-red-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xl font-display font-bold text-white">
                  Admin Dashboard
                </div>
                <div className="text-xs font-medium text-red-200">
                  LC Performance
                </div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-white/90 hover:text-white font-medium transition-colors flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Home
              </Link>
              <Link
                to="/account"
                className="text-white/90 hover:text-white font-medium transition-colors"
              >
                My Account
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">
                  {profile?.full_name || user?.email}
                </p>
                <p className="text-xs text-red-200">Administrator</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-white/90 hover:text-white font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Header */}
      <section className="bg-gradient-to-br from-red-900 via-red-800 to-red-700 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              <h1 className="text-4xl md:text-5xl font-display font-bold">
                Admin Dashboard
              </h1>
            </div>
            <p className="text-xl text-white/90">
              Manage clients, payments, notices, and programs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-red-600 border-b-2 border-red-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {rateLimitWarning && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Rate Limit Warning</p>
                <p className="text-sm text-yellow-700">{rateLimitWarning}</p>
              </div>
            </div>
          )}

          {activeTab === 'overview' && <AdminOverviewTab summary={summary} rateLimitStatus={rateLimitStatus} />}
          {activeTab === 'clients' && (
            <ClientsTab
              clients={clients}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'payments' && (
            <PaymentsTab
              payments={payments}
              pagination={paymentsPagination}
              onRefresh={loadAdminData}
              onPageChange={async (page, filters = {}) => {
                // Update filters if provided
                if (filters.startDate !== undefined || filters.endDate !== undefined || 
                    filters.status !== undefined || filters.provider !== undefined) {
                  setPaymentFilters(prev => ({ ...prev, ...filters }));
                }
                setPaymentsPagination(prev => ({ ...prev, page }));
                // Reload payments with new page and current filters
                if (user?.id) {
                  const allFilters = { ...paymentFilters, ...filters };
                  const result = await adminService.getAllPayments(user.id, {
                    page,
                    pageSize: paymentsPagination.pageSize,
                    ...allFilters,
                  });
                  if (result.success) {
                    setPayments(result.data);
                    if (result.pagination) {
                      setPaymentsPagination(result.pagination);
                    }
                  }
                }
              }}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'notices' && (
            <NoticesTab
              notices={notices}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'services' && (
            <ServicesTab
              services={services}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'blogs' && (
            <BlogsTab
              blogs={blogs}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'messages' && (
            <MessagesTab
              contacts={contacts}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
          {activeTab === 'programs' && (
            <ProgramsTab
              programs={programs}
              onRefresh={loadAdminData}
              adminUserId={user?.id}
            />
          )}
        </div>
      </section>
    </div>
  );
}

// Admin Overview Tab
function AdminOverviewTab({ summary, rateLimitStatus = {} }) {
  if (!summary) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No data available</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Clients</h3>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.totalClients}</p>
          <p className="text-sm text-gray-500 mt-2">
            {summary.activeClients} active users
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Revenue</h3>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(summary.totalRevenue)}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {summary.completedPayments} completed payments
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Payments</h3>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.pendingPayments}</p>
          <p className="text-sm text-gray-500 mt-2">
            {summary.totalPayments} total payments
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Programs</h3>
            <BookOpen className="w-8 h-8 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.activePrograms}</p>
          <p className="text-sm text-gray-500 mt-2">
            {summary.totalPrograms} total programs
          </p>
        </motion.div>
      </div>

      {/* Security Status */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-red-600" />
          Security Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Email Lookups</p>
            <p className="text-2xl font-bold text-gray-900">
              {rateLimitStatus.emailLookup?.remaining ?? 'N/A'} / {rateLimitStatus.emailLookup?.max ?? 'N/A'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {rateLimitStatus.emailLookup?.windowMinutes ?? 1} min window
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Admin Operations</p>
            <p className="text-2xl font-bold text-gray-900">
              {rateLimitStatus.adminOperation?.remaining ?? 'N/A'} / {rateLimitStatus.adminOperation?.max ?? 'N/A'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {rateLimitStatus.adminOperation?.windowMinutes ?? 1} min window
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Role Changes</p>
            <p className="text-2xl font-bold text-gray-900">
              {rateLimitStatus.roleChange?.remaining ?? 'N/A'} / {rateLimitStatus.roleChange?.max ?? 'N/A'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {rateLimitStatus.roleChange?.windowMinutes ?? 60} min window
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Payments</h3>
          <div className="space-y-3">
            {summary.recentPayments && summary.recentPayments.length > 0 ? (
              summary.recentPayments.slice(0, 5).map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {payment.user_profiles?.full_name || payment.user_profiles?.email || 'Unknown User'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                    payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {payment.status}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">No recent payments</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Unread Notices</h3>
          <div className="space-y-3">
            {summary.unreadNotices > 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-lg font-semibold text-yellow-900">
                  {summary.unreadNotices} unread notices
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {summary.globalNotices} global notices
                </p>
              </div>
            ) : (
              <p className="text-gray-600 text-center py-4">All notices read</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Clients Tab
function ClientsTab({ clients, onRefresh, adminUserId }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClientForManagement, setSelectedClientForManagement] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const filteredClients = clients.filter(client => {
    const matchesSearch = !searchTerm || 
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || client.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditForm({
      full_name: client.full_name || '',
      phone: client.phone || '',
      role: client.role || 'user',
    });
    setShowEditModal(true);
  };

  const handleManage = (client) => {
    setSelectedClientForManagement(client);
  };

  const handleSave = async () => {
    if (!selectedClient || !adminUserId) return;

    // Check rate limit for role changes if role is being changed
    if (editForm.role !== selectedClient.role) {
      const rateCheck = shouldAllowOperation('role_change');
      if (!rateCheck.allowed) {
        alert(rateCheck.error);
        return;
      }
      recordOperation('role_change');
    }

    setIsSaving(true);

    const result = await adminService.updateUserProfile(
      adminUserId,
      selectedClient.id,
      editForm
    );

    setIsSaving(false);

    if (result.success) {
      setShowEditModal(false);
      setSelectedClient(null);
      onRefresh();
    } else {
      const errorMsg = result.error || 'Failed to update client';
      if (errorMsg.includes('Rate limit')) {
        alert('⚠️ ' + errorMsg);
      } else {
        alert('Failed to update client: ' + errorMsg);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="user">Users</option>
            <option value="admin">Admins</option>
            <option value="editor">Editors</option>
          </select>
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                          <UserCheck className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {client.full_name || 'No name'}
                          </p>
                          {client.phone && (
                            <p className="text-sm text-gray-500">{client.phone}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        client.role === 'admin' ? 'bg-red-100 text-red-800' :
                        client.role === 'editor' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {client.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(client.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleManage(client)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Settings className="w-4 h-4" />
                          Manage
                        </button>
                        <button
                          onClick={() => handleEdit(client)}
                          className="text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Management Modal */}
      {selectedClientForManagement && (
        <ClientManagementModal
          client={selectedClientForManagement}
          adminUserId={adminUserId}
          isOpen={!!selectedClientForManagement}
          onClose={() => setSelectedClientForManagement(null)}
          onRefresh={onRefresh}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Edit Client</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="user">User</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// Payments Tab
function PaymentsTab({ payments, pagination, onRefresh, onPageChange, adminUserId }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    userId: '',
    provider: 'stripe',
    amount: '',
    currency: 'USD',
    status: 'pending',
    paymentType: 'one-time',
    description: '',
    renewalPeriod: 'monthly',
    customMonths: null,
    serviceId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  // Load services when component mounts or modal opens
  useEffect(() => {
    if (showCreateModal && adminUserId) {
      loadServices();
    }
  }, [showCreateModal, adminUserId]);

  const loadServices = async () => {
    if (!adminUserId) return;
    try {
      const result = await adminService.getAllServices(adminUserId);
      if (result.success) {
        setAvailableServices(result.data || []);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (filter === 'all') return true;
    if (filter === 'stripe') return payment.payment_provider === 'stripe';
    if (filter === 'affirm') return payment.payment_provider === 'affirm';
    return payment.status === filter;
  });

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const exportToCSV = () => {
    if (filteredPayments.length === 0) {
      alert('No payments to export');
      return;
    }

    // Create CSV headers
    const headers = ['Date', 'Client', 'Description', 'Provider', 'Amount', 'Currency', 'Status', 'Type'];
    const rows = filteredPayments.map(payment => [
      new Date(payment.created_at).toLocaleDateString(),
      payment.user_profiles?.full_name || payment.user_profiles?.email || 'Unknown',
      payment.description || 'Payment',
      payment.payment_provider,
      payment.amount,
      payment.currency || 'USD',
      payment.status,
      payment.payment_type,
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    // Check client-side rate limit (for UX feedback)
    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      alert(rateCheck.error);
      return;
    }

    // Validate Stripe configuration if using Stripe
    if (paymentForm.provider === 'stripe' && !isStripeConfigured) {
      alert('⚠️ Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.');
      return;
    }

    // Validate subscription renewal period
    if (paymentForm.paymentType === 'subscription') {
      if (!paymentForm.renewalPeriod) {
        alert('⚠️ Please select a renewal period for subscription payments.');
        return;
      }
      if (paymentForm.renewalPeriod === 'custom') {
        if (!paymentForm.customMonths || paymentForm.customMonths < 1 || paymentForm.customMonths > 24) {
          alert('⚠️ Please enter a valid number of months between 1 and 24 for custom renewal period.');
          return;
        }
      }
    }

    recordOperation('admin_operation');
    setIsSubmitting(true);

    // Look up user ID from email
    let userId = paymentForm.userId;
    if (paymentForm.userId.includes('@')) {
      const userResult = await adminService.getUserByEmail(adminUserId, paymentForm.userId);
      if (!userResult.success || !userResult.data) {
        alert('User not found: ' + paymentForm.userId);
        setIsSubmitting(false);
        return;
      }
      userId = userResult.data.id;
    }

    // For Stripe payments, always set status to pending
    // The client will complete the payment via Stripe Checkout
    const paymentStatus = paymentForm.provider === 'stripe' ? 'pending' : paymentForm.status;

    // Prepare metadata with service_id if service is selected
    const metadata = paymentForm.serviceId ? { service_id: paymentForm.serviceId } : {};

    const result = await adminService.createPayment(adminUserId, {
      userId: userId,
      provider: paymentForm.provider,
      providerPaymentId: `admin-${Date.now()}`,
      amount: parseFloat(paymentForm.amount),
      currency: paymentForm.currency,
      status: paymentStatus,
      paymentType: paymentForm.paymentType,
      description: paymentForm.description,
      renewalPeriod: paymentForm.paymentType === 'subscription' ? (paymentForm.renewalPeriod || 'monthly') : null,
      customMonths: paymentForm.paymentType === 'subscription' && paymentForm.renewalPeriod === 'custom' ? paymentForm.customMonths : null,
      metadata: metadata,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowCreateModal(false);
      const successMessage = paymentForm.provider === 'stripe' 
        ? `Payment request created! The client will see this as a pending payment in their account and can complete it via Stripe Checkout.`
        : 'Payment created successfully!';
      
      alert(successMessage);
      setPaymentForm({
        userId: '',
        provider: 'stripe',
        amount: '',
        currency: 'USD',
        status: 'pending',
        paymentType: 'one-time',
        description: '',
        renewalPeriod: 'monthly',
        customMonths: null,
        serviceId: '',
      });
      onRefresh();
    } else {
      const errorMsg = result.error || 'Failed to create payment';
      if (errorMsg.includes('Rate limit')) {
        alert('⚠️ ' + errorMsg);
      } else {
        alert('Failed to create payment: ' + errorMsg);
      }
    }
  };

  const handleUpdatePayment = async (updates) => {
    if (!adminUserId || !selectedPayment) return;

    const result = await adminService.updatePayment(adminUserId, selectedPayment.id, updates);
    if (result.success) {
      setShowEditModal(false);
      setSelectedPayment(null);
      onRefresh();
    } else {
      alert('Failed to update payment: ' + result.error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">All Payments</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Payment
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('stripe')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'stripe' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Stripe
            </button>
            <button
              onClick={() => setFilter('affirm')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'affirm' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Affirm
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'pending' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'completed' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <Calendar className="w-4 h-4" />
              Date Range
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-red-600 text-white hover:bg-red-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        {showDateFilter && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => {
                    const newDateRange = { ...dateRange, startDate: e.target.value };
                    setDateRange(newDateRange);
                    // Reload with date filter
                    if (onPageChange && adminUserId) {
                      onPageChange(1, {
                        startDate: newDateRange.startDate || null,
                        endDate: newDateRange.endDate || null,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => {
                    const newDateRange = { ...dateRange, endDate: e.target.value };
                    setDateRange(newDateRange);
                    // Reload with date filter
                    if (onPageChange && adminUserId) {
                      onPageChange(1, {
                        startDate: newDateRange.startDate || null,
                        endDate: newDateRange.endDate || null,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={() => {
                  setDateRange({ startDate: '', endDate: '' });
                  if (onPageChange && adminUserId) {
                    onPageChange(1, {
                      startDate: null,
                      endDate: null,
                    });
                  }
                }}
                className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
              >
                Clear date filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Renewal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {payment.user_profiles?.full_name || payment.user_profiles?.email || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {payment.description || 'Payment'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {payment.payment_provider}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount, payment.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {payment.payment_type === 'subscription' ? (
                        <div className="flex flex-col gap-1">
                          {payment.renewal_period && (
                            <span className="text-xs">
                              {formatRenewalPeriod(payment.renewal_period, payment.renewal_interval_months)}
                            </span>
                          )}
                          {payment.renewal_date && (
                            <span className="text-xs text-gray-500">
                              {payment.status === 'pending' ? 'Renews: ' : 'Next: '}
                              {formatRenewalDate(payment.renewal_date)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowEditModal(true);
                        }}
                        className="text-red-600 hover:text-red-800 flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total} payments
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange && onPageChange(pagination.page - 1)}
                disabled={!pagination.hasPreviousPage}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  pagination.hasPreviousPage
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange && onPageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                        pagination.page === pageNum
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange && onPageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  pagination.hasNextPage
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Payment Modal */}
      {showCreateModal && (
        <CreatePaymentModal
          form={paymentForm}
          setForm={setPaymentForm}
          onSubmit={handleCreatePayment}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isSubmitting}
          availableServices={availableServices}
        />
      )}

      {/* Edit Payment Modal */}
      {showEditModal && selectedPayment && (
        <EditPaymentModal
          payment={selectedPayment}
          onUpdate={handleUpdatePayment}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPayment(null);
          }}
        />
      )}
    </div>
  );
}

// Create Payment Modal
function CreatePaymentModal({ form, setForm, onSubmit, onClose, isSubmitting, availableServices = [] }) {
  const handleServiceChange = (serviceId) => {
    if (!serviceId) {
      setForm({ ...form, serviceId: '' });
      return;
    }

    const service = availableServices.find(s => s.id === serviceId);
    if (service) {
      setForm({
        ...form,
        serviceId: service.id,
        amount: service.price.toString(),
        currency: service.currency || 'USD',
        paymentType: 'subscription',
        description: service.name,
        renewalPeriod: service.renewal_period || 'monthly',
        customMonths: service.renewal_period === 'custom' ? service.renewal_interval_months : null,
      });
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Create Payment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="user@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">Enter user email to find user ID</p>
          </div>

          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service (Optional)
            </label>
            <select
              value={form.serviceId || ''}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Select a service (or create manually)</option>
              {availableServices.filter(s => s.is_active).map(service => (
                <option key={service.id} value={service.id}>
                  {service.name} - ${parseFloat(service.price).toLocaleString()}{getServicePriceSuffix(service.renewal_period, service.renewal_interval_months)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Selecting a service will auto-populate amount, description, and renewal period</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider
              </label>
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="stripe">Stripe</option>
                <option value="affirm">Affirm</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Stripe Payment Info */}
          {form.provider === 'stripe' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Stripe Payment</p>
                  <p className="text-xs">
                    This payment will be created as "pending". The client will see it in their account 
                    and can complete it via Stripe Checkout. Status will update automatically when payment is processed.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                disabled={form.provider === 'stripe'}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  form.provider === 'stripe' ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
              {form.provider === 'stripe' && (
                <p className="text-xs text-gray-500 mt-1">Status is automatically set to "pending" for Stripe payments</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Type
              </label>
              <select
                value={form.paymentType}
                onChange={(e) => {
                  const newPaymentType = e.target.value;
                  setForm({ 
                    ...form, 
                    paymentType: newPaymentType,
                    // Always default to monthly when switching to subscription
                    renewalPeriod: newPaymentType === 'subscription' ? (form.renewalPeriod || 'monthly') : null,
                    customMonths: newPaymentType === 'subscription' && form.renewalPeriod === 'custom' ? form.customMonths : null,
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="one-time">One-time</option>
                <option value="subscription">Subscription</option>
                <option value="installment">Installment</option>
              </select>
            </div>
          </div>

          {/* Subscription Renewal Period Fields */}
          {form.paymentType === 'subscription' && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                <label className="block text-sm font-medium text-gray-700">
                  Renewal Period <span className="text-red-500">*</span>
                </label>
              </div>
              
              <div>
                <select
                  required={form.paymentType === 'subscription'}
                  value={form.renewalPeriod || 'monthly'}
                  onChange={(e) => {
                    const newPeriod = e.target.value;
                    setForm({ 
                      ...form, 
                      renewalPeriod: newPeriod,
                      customMonths: newPeriod === 'custom' ? (form.customMonths || 1) : null,
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="3months">Every 3 Months</option>
                  <option value="custom">Custom (Months)</option>
                </select>
              </div>

              {/* Custom Months Input */}
              {form.renewalPeriod === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Months <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    required={form.renewalPeriod === 'custom'}
                    value={form.customMonths || ''}
                    onChange={(e) => {
                      const months = parseInt(e.target.value) || null;
                      if (months !== null && months >= 1 && months <= 24) {
                        setForm({ ...form, customMonths: months });
                      } else if (e.target.value === '') {
                        setForm({ ...form, customMonths: null });
                      }
                    }}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                      form.renewalPeriod === 'custom' && (!form.customMonths || form.customMonths < 1 || form.customMonths > 24)
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter months (1-24)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter a number between 1 and 24 months</p>
                  {form.renewalPeriod === 'custom' && form.customMonths !== null && (form.customMonths < 1 || form.customMonths > 24) && (
                    <p className="text-xs text-red-600 mt-1">Please enter a value between 1 and 24</p>
                  )}
                </div>
              )}

              {/* Renewal Date Preview */}
              {form.renewalPeriod && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-800 mb-1">Renewal Information</p>
                  <p className="text-sm text-blue-700">
                    Period: <span className="font-medium">{formatRenewalPeriod(form.renewalPeriod, form.customMonths)}</span>
                  </p>
                  {form.renewalPeriod && (form.renewalPeriod !== 'custom' || form.customMonths) && (
                    <p className="text-sm text-blue-700">
                      Next Renewal: <span className="font-medium">
                        {formatRenewalDate(calculateRenewalDate(form.renewalPeriod, form.customMonths))}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Payment description..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Payment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Edit Payment Modal
function EditPaymentModal({ payment, onUpdate, onClose }) {
  const [status, setStatus] = useState(payment.status);

  const handleSave = () => {
    onUpdate({ status });
  };

  // Format renewal information for display
  const renewalInfo = payment.payment_type === 'subscription' && payment.renewal_period
    ? formatRenewalPeriod(payment.renewal_period, payment.renewal_interval_months)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Edit Payment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600">Amount</p>
            <p className="text-lg font-semibold text-gray-900">
              ${payment.amount} {payment.currency}
            </p>
          </div>

          {payment.payment_type === 'subscription' && (
            <div>
              <p className="text-sm text-gray-600">Payment Type</p>
              <p className="text-sm font-medium text-gray-900">Subscription</p>
            </div>
          )}

          {renewalInfo && (
            <div>
              <p className="text-sm text-gray-600">Renewal Period</p>
              <p className="text-sm font-medium text-gray-900">{renewalInfo}</p>
            </div>
          )}

          {payment.renewal_date && (
            <div>
              <p className="text-sm text-gray-600">
                {payment.status === 'pending' ? 'Renews On' : 'Next Renewal'}
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatRenewalDate(payment.renewal_date)}
              </p>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-600">Client</p>
            <p className="text-lg font-semibold text-gray-900">
              {payment.user_profiles?.full_name || payment.user_profiles?.email || 'Unknown'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Save Changes
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Notices Tab
function NoticesTab({ notices, onRefresh, adminUserId }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [noticeForm, setNoticeForm] = useState({
    userId: '',
    title: '',
    message: '',
    type: 'info',
    isGlobal: false,
    actionUrl: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateNotice = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    // Check client-side rate limit
    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      alert(rateCheck.error);
      return;
    }

    recordOperation('admin_operation');
    setIsSubmitting(true);

    // Look up user ID from email if provided
    let userId = null;
    if (noticeForm.userId && !noticeForm.isGlobal) {
      if (noticeForm.userId.includes('@')) {
        const userResult = await adminService.getUserByEmail(adminUserId, noticeForm.userId);
        if (!userResult.success || !userResult.data) {
          alert('User not found: ' + noticeForm.userId);
          setIsSubmitting(false);
          return;
        }
        userId = userResult.data.id;
      } else {
        userId = noticeForm.userId;
      }
    }

    const result = await adminService.createNotice(adminUserId, {
      userId: userId,
      title: noticeForm.title,
      message: noticeForm.message,
      type: noticeForm.type,
      isGlobal: noticeForm.isGlobal,
      actionUrl: noticeForm.actionUrl || null,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowCreateModal(false);
      setNoticeForm({
        userId: '',
        title: '',
        message: '',
        type: 'info',
        isGlobal: false,
        actionUrl: '',
      });
      onRefresh();
    } else {
      const errorMsg = result.error || 'Failed to create notice';
      if (errorMsg.includes('Rate limit')) {
        alert('⚠️ ' + errorMsg);
      } else {
        alert('Failed to create notice: ' + errorMsg);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">All Notices</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Notice
        </button>
      </div>

      <div className="space-y-4">
        {notices.length > 0 ? (
          notices.map((notice) => (
            <motion.div
              key={notice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl shadow-lg p-6 ${
                notice.is_global ? 'border-l-4 border-yellow-500' : 'border-l-4 border-blue-500'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Bell className={`w-5 h-5 ${
                      notice.is_global ? 'text-yellow-600' : 'text-blue-600'
                    }`} />
                    <h3 className="text-lg font-semibold text-gray-900">{notice.title}</h3>
                    {notice.is_global && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        Global
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-3">{notice.message}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>
                      {notice.user_profiles ? (
                        `To: ${notice.user_profiles.full_name || notice.user_profiles.email}`
                      ) : (
                        'To: All Users'
                      )}
                    </span>
                    <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                    <span className="capitalize">{notice.type}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    notice.is_read ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {notice.is_read ? 'Read' : 'Unread'}
                  </span>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No notices available</p>
          </div>
        )}
      </div>

      {/* Create Notice Modal */}
      {showCreateModal && (
        <CreateNoticeModal
          form={noticeForm}
          setForm={setNoticeForm}
          onSubmit={handleCreateNotice}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}

// Create Notice Modal
function CreateNoticeModal({ form, setForm, onSubmit, onClose, isSubmitting }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Create Notice</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isGlobal"
              checked={form.isGlobal}
              onChange={(e) => setForm({ ...form, isGlobal: e.target.checked, userId: '' })}
              className="w-4 h-4 text-red-600 focus:ring-red-500"
            />
            <label htmlFor="isGlobal" className="text-sm font-medium text-gray-700">
              Global notice (visible to all users)
            </label>
          </div>

          {!form.isGlobal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Email (leave empty for global)
              </label>
              <input
                type="email"
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="user@example.com"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Notice title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Notice message..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="update">Update</option>
                <option value="system">System</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action URL (optional)
            </label>
            <input
              type="url"
              value={form.actionUrl}
              onChange={(e) => setForm({ ...form, actionUrl: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="https://..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Notice'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Programs Tab
function ProgramsTab({ programs, onRefresh, adminUserId }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [programForm, setProgramForm] = useState({
    userId: '',
    programName: '',
    programType: 'coaching',
    status: 'active',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    coachNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    // Check client-side rate limit
    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      alert(rateCheck.error);
      return;
    }

    recordOperation('admin_operation');
    setIsSubmitting(true);

    // Look up user ID from email
    let userId = programForm.userId;
    if (programForm.userId.includes('@')) {
      const userResult = await adminService.getUserByEmail(adminUserId, programForm.userId);
      if (!userResult.success || !userResult.data) {
        alert('User not found: ' + programForm.userId);
        setIsSubmitting(false);
        return;
      }
      userId = userResult.data.id;
    }

    const result = await adminService.upsertProgram(adminUserId, {
      userId: userId,
      programName: programForm.programName,
      programType: programForm.programType,
      status: programForm.status,
      startDate: programForm.startDate,
      endDate: programForm.endDate || null,
      coachNotes: programForm.coachNotes,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowCreateModal(false);
      setProgramForm({
        userId: '',
        programName: '',
        programType: 'coaching',
        status: 'active',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        coachNotes: '',
      });
      onRefresh();
    } else {
      const errorMsg = result.error || 'Failed to create program';
      if (errorMsg.includes('Rate limit')) {
        alert('⚠️ ' + errorMsg);
      } else {
        alert('Failed to create program: ' + errorMsg);
      }
    }
  };

  const handleEdit = (program) => {
    setSelectedProgram(program);
    setProgramForm({
      userId: program.user_id,
      programName: program.program_name,
      programType: program.program_type,
      status: program.status,
      startDate: program.start_date,
      endDate: program.end_date || '',
      coachNotes: program.coach_notes || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateProgram = async () => {
    if (!adminUserId || !selectedProgram) return;

    setIsSubmitting(true);
    const result = await adminService.upsertProgram(adminUserId, {
      id: selectedProgram.id,
      userId: programForm.userId,
      programName: programForm.programName,
      programType: programForm.programType,
      status: programForm.status,
      startDate: programForm.startDate,
      endDate: programForm.endDate || null,
      coachNotes: programForm.coachNotes,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowEditModal(false);
      setSelectedProgram(null);
      onRefresh();
    } else {
      alert('Failed to update program: ' + result.error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">All Programs</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Program
        </button>
      </div>

      <div className="space-y-4">
        {programs.length > 0 ? (
          programs.map((program) => (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {program.program_name}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    Client: {program.user_profiles?.full_name || program.user_profiles?.email || 'Unknown'}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="capitalize">{program.program_type.replace('_', ' ')}</span>
                    <span>Start: {new Date(program.start_date).toLocaleDateString()}</span>
                    {program.end_date && (
                      <span>End: {new Date(program.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(program.status)}`}>
                    {program.status}
                  </span>
                  <button
                    onClick={() => handleEdit(program)}
                    className="text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              </div>
              {program.coach_notes && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Coach Notes:</p>
                  <p className="text-sm text-gray-600">{program.coach_notes}</p>
                </div>
              )}
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No programs found</p>
          </div>
        )}
      </div>

      {/* Create Program Modal */}
      {showCreateModal && (
        <CreateProgramModal
          form={programForm}
          setForm={setProgramForm}
          onSubmit={handleCreateProgram}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Edit Program Modal */}
      {showEditModal && (
        <EditProgramModal
          form={programForm}
          setForm={setProgramForm}
          onSubmit={handleUpdateProgram}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProgram(null);
          }}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}

// Create Program Modal
function CreateProgramModal({ form, setForm, onSubmit, onClose, isSubmitting }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Create Program</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Program Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.programName}
              onChange={(e) => setForm({ ...form, programName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="e.g., 12-Week Transformation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Type
              </label>
              <select
                value={form.programType}
                onChange={(e) => setForm({ ...form, programType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="coaching">Coaching</option>
                <option value="diet_plan">Diet Plan</option>
                <option value="training_protocol">Training Protocol</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coach Notes
            </label>
            <textarea
              value={form.coachNotes}
              onChange={(e) => setForm({ ...form, coachNotes: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Program notes and instructions..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Program'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Edit Program Modal
function EditProgramModal({ form, setForm, onSubmit, onClose, isSubmitting }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Edit Program</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Program Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.programName}
              onChange={(e) => setForm({ ...form, programName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Type
              </label>
              <select
                value={form.programType}
                onChange={(e) => setForm({ ...form, programType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="coaching">Coaching</option>
                <option value="diet_plan">Diet Plan</option>
                <option value="training_protocol">Training Protocol</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coach Notes
            </label>
            <textarea
              value={form.coachNotes}
              onChange={(e) => setForm({ ...form, coachNotes: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Services Tab
function ServicesTab({ services, onRefresh, adminUserId }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [serviceForm, setServiceForm] = useState({
    name: '',
    tagline: '',
    description: '',
    icon_name: 'Users',
    color: 'blue',
    price: '',
    currency: 'USD',
    renewal_period: 'monthly',
    renewal_interval_months: null,
    features: [{ name: '', included: true }],
    max_students: '',
    is_popular: false,
    is_active: true,
    display_order: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Available Lucide icons for selection
  const availableIcons = [
    'Users', 'Star', 'MapPin', 'Crown', 'Zap', 'Calendar', 'Video', 
    'Globe', 'MessageCircle', 'Dumbbell', 'Check', 'X', 'Package'
  ];

  const filteredServices = services.filter(service => {
    const matchesSearch = !searchTerm || 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.tagline.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterActive === 'all' || 
      (filterActive === 'active' && service.is_active) ||
      (filterActive === 'inactive' && !service.is_active);
    return matchesSearch && matchesFilter;
  });

  const handleCreateService = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    // Validate required fields
    if (!serviceForm.name || !serviceForm.tagline || !serviceForm.price || !serviceForm.renewal_period) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate custom renewal period
    if (serviceForm.renewal_period === 'custom') {
      if (!serviceForm.renewal_interval_months || serviceForm.renewal_interval_months < 1 || serviceForm.renewal_interval_months > 24) {
        alert('Custom renewal period requires 1-24 months');
        return;
      }
    }

    // Validate features
    const validFeatures = serviceForm.features.filter(f => f.name.trim() !== '');
    if (validFeatures.length === 0) {
      alert('Please add at least one feature');
      return;
    }

    setIsSubmitting(true);

    const result = await adminService.createService(adminUserId, {
      ...serviceForm,
      price: parseFloat(serviceForm.price),
      renewal_interval_months: serviceForm.renewal_period === 'custom' ? parseInt(serviceForm.renewal_interval_months) : null,
      features: validFeatures,
      display_order: parseInt(serviceForm.display_order) || 0,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowCreateModal(false);
      resetForm();
      onRefresh();
    } else {
      alert('Failed to create service: ' + (result.error || 'Unknown error'));
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!adminUserId || !selectedService) return;

    // Validate required fields
    if (!serviceForm.name || !serviceForm.tagline || !serviceForm.price || !serviceForm.renewal_period) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate custom renewal period
    if (serviceForm.renewal_period === 'custom') {
      if (!serviceForm.renewal_interval_months || serviceForm.renewal_interval_months < 1 || serviceForm.renewal_interval_months > 24) {
        alert('Custom renewal period requires 1-24 months');
        return;
      }
    }

    // Validate features
    const validFeatures = serviceForm.features.filter(f => f.name.trim() !== '');
    if (validFeatures.length === 0) {
      alert('Please add at least one feature');
      return;
    }

    setIsSubmitting(true);

    const result = await adminService.updateService(adminUserId, selectedService.id, {
      ...serviceForm,
      price: parseFloat(serviceForm.price),
      renewal_interval_months: serviceForm.renewal_period === 'custom' ? parseInt(serviceForm.renewal_interval_months) : null,
      features: validFeatures,
      display_order: parseInt(serviceForm.display_order) || 0,
    });

    setIsSubmitting(false);

    if (result.success) {
      setShowEditModal(false);
      setSelectedService(null);
      resetForm();
      onRefresh();
    } else {
      alert('Failed to update service: ' + (result.error || 'Unknown error'));
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!adminUserId) return;
    if (!confirm('Are you sure you want to delete this service? It will be hidden from the public page.')) {
      return;
    }

    const result = await adminService.deleteService(adminUserId, serviceId);
    if (result.success) {
      onRefresh();
    } else {
      alert('Failed to delete service: ' + (result.error || 'Unknown error'));
    }
  };

  const resetForm = () => {
    setServiceForm({
      name: '',
      tagline: '',
      description: '',
      icon_name: 'Users',
      color: 'blue',
      price: '',
      currency: 'USD',
      renewal_period: 'monthly',
      renewal_interval_months: null,
      features: [{ name: '', included: true }],
      max_students: '',
      is_popular: false,
      is_active: true,
      display_order: 0,
    });
  };

  const openEditModal = (service) => {
    setSelectedService(service);
    setServiceForm({
      name: service.name || '',
      tagline: service.tagline || '',
      description: service.description || '',
      icon_name: service.icon_name || 'Users',
      color: service.color || 'blue',
      price: service.price?.toString() || '',
      currency: service.currency || 'USD',
      renewal_period: service.renewal_period || 'monthly',
      renewal_interval_months: service.renewal_interval_months || null,
      features: Array.isArray(service.features) && service.features.length > 0 
        ? service.features 
        : [{ name: '', included: true }],
      max_students: service.max_students || '',
      is_popular: service.is_popular || false,
      is_active: service.is_active !== undefined ? service.is_active : true,
      display_order: service.display_order || 0,
    });
    setShowEditModal(true);
  };

  const addFeature = () => {
    setServiceForm(prev => ({
      ...prev,
      features: [...prev.features, { name: '', included: true }]
    }));
  };

  const removeFeature = (index) => {
    setServiceForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, field, value) => {
    setServiceForm(prev => ({
      ...prev,
      features: prev.features.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Services Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Service
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
        >
          <option value="all">All Services</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Term</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredServices.length > 0 ? (
                filteredServices.map((service) => {
                  const priceSuffix = getServicePriceSuffix(service.renewal_period, service.renewal_interval_months);
                  return (
                    <tr key={service.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${
                            service.color === 'blue' ? 'from-blue-500 to-blue-600' :
                            service.color === 'purple' ? 'from-purple-500 to-purple-600' :
                            service.color === 'primary' ? 'from-primary-500 to-primary-600' :
                            'from-accent-500 to-accent-600'
                          }`}>
                            <Package className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-sm text-gray-500">{service.tagline}</div>
                          </div>
                          {service.is_popular && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                              Popular
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ${parseFloat(service.price).toLocaleString()}{priceSuffix}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatServiceTerm(service.renewal_period, service.renewal_interval_months)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          service.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {service.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.display_order}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(service)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No services found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Service Modal */}
      {showCreateModal && (
        <ServiceModal
          serviceForm={serviceForm}
          setServiceForm={setServiceForm}
          availableIcons={availableIcons}
          addFeature={addFeature}
          removeFeature={removeFeature}
          updateFeature={updateFeature}
          onSubmit={handleCreateService}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          isSubmitting={isSubmitting}
          title="Create Service"
        />
      )}

      {/* Edit Service Modal */}
      {showEditModal && (
        <ServiceModal
          serviceForm={serviceForm}
          setServiceForm={setServiceForm}
          availableIcons={availableIcons}
          addFeature={addFeature}
          removeFeature={removeFeature}
          updateFeature={updateFeature}
          onSubmit={handleUpdateService}
          onClose={() => {
            setShowEditModal(false);
            setSelectedService(null);
            resetForm();
          }}
          isSubmitting={isSubmitting}
          title="Edit Service"
        />
      )}
    </div>
  );
}

// Service Modal Component
function ServiceModal({ 
  serviceForm, 
  setServiceForm, 
  availableIcons, 
  addFeature, 
  removeFeature, 
  updateFeature,
  onSubmit, 
  onClose, 
  isSubmitting,
  title 
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={serviceForm.name}
                onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tagline <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={serviceForm.tagline}
                onChange={(e) => setServiceForm(prev => ({ ...prev, tagline: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={serviceForm.description}
              onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Visual Settings */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon <span className="text-red-500">*</span>
              </label>
              <select
                value={serviceForm.icon_name}
                onChange={(e) => setServiceForm(prev => ({ ...prev, icon_name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Theme <span className="text-red-500">*</span>
              </label>
              <select
                value={serviceForm.color}
                onChange={(e) => setServiceForm(prev => ({ ...prev, color: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                <option value="blue">Blue</option>
                <option value="purple">Purple</option>
                <option value="primary">Primary (Red)</option>
                <option value="accent">Accent (Orange)</option>
              </select>
            </div>
          </div>

          {/* Pricing */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={serviceForm.price}
                onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={serviceForm.currency}
                onChange={(e) => setServiceForm(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Students
              </label>
              <input
                type="text"
                value={serviceForm.max_students}
                onChange={(e) => setServiceForm(prev => ({ ...prev, max_students: e.target.value }))}
                placeholder="e.g., 50 spots available"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Term Length */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Term Length <span className="text-red-500">*</span>
            </label>
            <select
              value={serviceForm.renewal_period}
              onChange={(e) => {
                const newPeriod = e.target.value;
                setServiceForm(prev => ({
                  ...prev,
                  renewal_period: newPeriod,
                  renewal_interval_months: newPeriod === 'custom' ? prev.renewal_interval_months : null
                }));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-2"
              required
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="3months">3 Months</option>
              <option value="6months">6 Months</option>
              <option value="yearly">Yearly</option>
              <option value="custom">Custom (Months)</option>
            </select>
            {serviceForm.renewal_period === 'custom' && (
              <input
                type="number"
                min="1"
                max="24"
                value={serviceForm.renewal_interval_months || ''}
                onChange={(e) => setServiceForm(prev => ({ ...prev, renewal_interval_months: parseInt(e.target.value) || null }))}
                placeholder="Number of months (1-24)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mt-2"
                required={serviceForm.renewal_period === 'custom'}
              />
            )}
            {serviceForm.renewal_period && (
              <p className="text-xs text-gray-500 mt-1">
                Term: {formatServiceTerm(serviceForm.renewal_period, serviceForm.renewal_interval_months)} 
                {serviceForm.price && ` - $${parseFloat(serviceForm.price).toLocaleString()}${getServicePriceSuffix(serviceForm.renewal_period, serviceForm.renewal_interval_months)}`}
              </p>
            )}
          </div>

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features
            </label>
            <div className="space-y-2">
              {serviceForm.features.map((feature, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="checkbox"
                    checked={feature.included}
                    onChange={(e) => updateFeature(index, 'included', e.target.checked)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <input
                    type="text"
                    value={feature.name}
                    onChange={(e) => updateFeature(index, 'name', e.target.value)}
                    placeholder="Feature name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  {serviceForm.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </button>
            </div>
          </div>

          {/* Settings */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={serviceForm.is_popular}
                onChange={(e) => setServiceForm(prev => ({ ...prev, is_popular: e.target.checked }))}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="text-sm font-medium text-gray-700">Show Popular Badge</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={serviceForm.is_active}
                onChange={(e) => setServiceForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="text-sm font-medium text-gray-700">Active (Visible on public page)</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Order
              </label>
              <input
                type="number"
                min="0"
                value={serviceForm.display_order}
                onChange={(e) => setServiceForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Service'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Blogs Tab Component
function BlogsTab({ blogs, onRefresh, adminUserId }) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = () => {
    setEditingBlog(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setIsEditorOpen(true);
  };

  const handleDelete = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog post? It will be unpublished.')) {
      return;
    }

    setIsDeleting(blogId);
    try {
      const result = await adminService.deleteBlog(adminUserId, blogId);
      if (result.success) {
        onRefresh();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog post');
    } finally {
      setIsDeleting(null);
    }
  };

  const handlePublishToggle = async (blog) => {
    try {
      const result = await adminService.publishBlog(adminUserId, blog.id, !blog.is_published);
      if (result.success) {
        onRefresh();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update blog status');
    }
  };

  const handleSave = async (blogData) => {
    setIsSaving(true);
    try {
      let result;
      if (editingBlog) {
        result = await adminService.updateBlog(adminUserId, editingBlog.id, blogData);
      } else {
        result = await adminService.createBlog(adminUserId, blogData);
      }

      if (result.success) {
        setIsEditorOpen(false);
        setEditingBlog(null);
        onRefresh();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Failed to save blog post');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog Posts</h2>
          <p className="text-gray-600 mt-1">Manage blog posts and content</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Blog Post
        </button>
      </div>

      {/* Blog Editor Modal */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <BlogEditor
              blog={editingBlog}
              onSave={handleSave}
              onCancel={() => {
                setIsEditorOpen(false);
                setEditingBlog(null);
              }}
              userId={adminUserId}
            />
          </div>
        </div>
      )}

      {/* Blogs Table */}
      {blogs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No blog posts yet</p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Create Your First Blog Post
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                      <div className="text-sm text-gray-500">/{blog.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {blog.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          blog.is_published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {blog.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(blog.published_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {blog.view_count || 0}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePublishToggle(blog)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            blog.is_published
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {blog.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          onClick={() => handleEdit(blog)}
                          className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                          aria-label="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id)}
                          disabled={isDeleting === blog.id}
                          className="p-2 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                          aria-label="Delete"
                        >
                          {isDeleting === blog.id ? (
                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Messages Tab Component (Contact Requests)
function MessagesTab({ contacts, onRefresh, adminUserId }) {
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isUpdating, setIsUpdating] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);

  const handleView = (contact) => {
    setSelectedContact(contact);
    // Mark as read if status is 'new'
    if (contact.status === 'new') {
      handleStatusChange(contact.id, 'read');
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    setIsUpdating(contactId);
    try {
      const result = await adminService.updateContact(adminUserId, contactId, {
        status: newStatus,
      });
      if (result.success) {
        onRefresh();
        if (selectedContact?.id === contactId) {
          setSelectedContact({ ...selectedContact, status: newStatus });
        }
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
      alert('Failed to update contact status');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleDelete = async (contactId) => {
    if (!confirm('Are you sure you want to delete this message?')) {
      return;
    }

    setIsDeleting(contactId);
    try {
      const result = await adminService.deleteContact(adminUserId, contactId);
      if (result.success) {
        if (selectedContact?.id === contactId) {
          setSelectedContact(null);
        }
        onRefresh();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete message');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-gray-100 text-gray-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredContacts = statusFilter === 'all'
    ? contacts
    : contacts.filter(c => c.status === statusFilter);

  const statusCounts = {
    all: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    read: contacts.filter(c => c.status === 'read').length,
    replied: contacts.filter(c => c.status === 'replied').length,
    archived: contacts.filter(c => c.status === 'archived').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Messages</h2>
          <p className="text-gray-600 mt-1">View and manage contact form submissions</p>
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All', count: statusCounts.all },
          { id: 'new', label: 'New', count: statusCounts.new },
          { id: 'read', label: 'Read', count: statusCounts.read },
          { id: 'replied', label: 'Replied', count: statusCounts.replied },
          { id: 'archived', label: 'Archived', count: statusCounts.archived },
        ].map((filter) => (
          <button
            key={filter.id}
            onClick={() => setStatusFilter(filter.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === filter.id
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {filter.label} ({filter.count})
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">
              Messages ({filteredContacts.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p>No messages found</p>
              </div>
            ) : (
              filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => handleView(contact)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                      <p className="text-sm text-gray-600">{contact.email}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}>
                      {contact.status}
                    </span>
                  </div>
                  {contact.subject && (
                    <p className="text-sm font-medium text-gray-700 mb-1">{contact.subject}</p>
                  )}
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {contact.message}
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(contact.created_at)}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {selectedContact ? (
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {selectedContact.name}
                  </h3>
                  <p className="text-gray-600">{selectedContact.email}</p>
                  {selectedContact.phone && (
                    <p className="text-gray-600">{selectedContact.phone}</p>
                  )}
                </div>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedContact.status)}`}>
                  {selectedContact.status}
                </span>
              </div>

              <div className="space-y-4 mb-6">
                {selectedContact.subject && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">Subject/Program</h4>
                    <p className="text-gray-900">{selectedContact.subject}</p>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Message</h4>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>

                <div className="text-sm text-gray-500">
                  <p>Received: {formatDate(selectedContact.created_at)}</p>
                  {selectedContact.source && (
                    <p>Source: {selectedContact.source}</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex gap-2">
                  <select
                    value={selectedContact.status}
                    onChange={(e) => handleStatusChange(selectedContact.id, e.target.value)}
                    disabled={isUpdating === selectedContact.id}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="replied">Replied</option>
                    <option value="archived">Archived</option>
                  </select>
                  <button
                    onClick={() => handleDelete(selectedContact.id)}
                    disabled={isDeleting === selectedContact.id}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isDeleting === selectedContact.id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </>
                    )}
                  </button>
                </div>

                <div className="flex gap-2">
                  <a
                    href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject || 'Your Message'}`}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-center flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Reply via Email
                  </a>
                  {selectedContact.phone && (
                    <a
                      href={`tel:${selectedContact.phone}`}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p>Select a message to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
